import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  FileText, 
  Download, 
  Calendar,
  ArrowDownRight,
  ArrowUpRight,
  RefreshCw,
  TrendingUp,
  Wallet,
  Filter,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MONTHS_PT = [
  '', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const TYPE_CONFIG = {
  deposit: { label: 'Depósito', color: 'text-emerald-400', bg: 'bg-emerald-500/10', icon: ArrowDownRight },
  withdrawal: { label: 'Levantamento', color: 'text-amber-400', bg: 'bg-amber-500/10', icon: ArrowUpRight },
  utility_payment: { label: 'Pagamento Utilidade', color: 'text-blue-400', bg: 'bg-blue-500/10', icon: Wallet },
  reinvestment: { label: 'Reinvestimento', color: 'text-purple-400', bg: 'bg-purple-500/10', icon: RefreshCw },
  adjustment: { label: 'Ajuste', color: 'text-slate-400', bg: 'bg-slate-500/10', icon: TrendingUp },
  credit: { label: 'Crédito', color: 'text-emerald-400', bg: 'bg-emerald-500/10', icon: ArrowDownRight },
  debit: { label: 'Débito', color: 'text-red-400', bg: 'bg-red-500/10', icon: ArrowUpRight },
  profit: { label: 'Lucro', color: 'text-emerald-400', bg: 'bg-emerald-500/10', icon: TrendingUp }
};

export default function MonthlyStatement() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchStatement();
  }, [year, month, page]);

  const fetchStatement = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/statement/monthly?year=${year}&month=${month}&page=${page}&limit=20`);
      setData(response.data);
    } catch (error) {
      toast.error('Erro ao carregar extracto');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(
        `${API_URL}/admin/statement/monthly/pdf?year=${year}&month=${month}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `extracto_sgfi_${MONTHS_PT[month].toLowerCase()}_${year}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF descarregado com sucesso');
    } catch (error) {
      toast.error('Erro ao gerar PDF');
    } finally {
      setDownloading(false);
    }
  };

  const goToPreviousMonth = () => {
    setPage(1);
    setFilterType('all');
    if (month === 1) {
      setMonth(12);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };

  const goToNextMonth = () => {
    setPage(1);
    setFilterType('all');
    if (month === 12) {
      setMonth(1);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  const filteredTransactions = data?.transactions?.filter(t => 
    filterType === 'all' || t.type === filterType
  ) || [];

  const pagination = data?.pagination || {};

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(value);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('pt-PT', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="animate-fade-in" data-testid="monthly-statement-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Outfit']">Extracto Mensal</h1>
          <p className="text-slate-400 text-sm">Visualize todos os movimentos do mês</p>
        </div>
        
        <Button
          onClick={handleDownloadPDF}
          disabled={downloading || loading}
          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
          data-testid="download-pdf-btn"
        >
          {downloading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Download className="w-4 h-4 mr-2" />
          )}
          Descarregar PDF
        </Button>
      </div>

      {/* Month Selector */}
      <div className="glass-card p-4 mb-6">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={goToPreviousMonth}
            className="text-slate-400 hover:text-white"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-blue-400" />
            <span className="text-xl font-semibold text-white">
              {MONTHS_PT[month]} {year}
            </span>
          </div>
          
          <Button
            variant="ghost"
            onClick={goToNextMonth}
            className="text-slate-400 hover:text-white"
            disabled={month === now.getMonth() + 1 && year === now.getFullYear()}
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="glass-card p-4">
              <div className="flex items-center gap-2 text-emerald-400 mb-2">
                <ArrowDownRight className="w-4 h-4" />
                <span className="text-xs">Depósitos</span>
              </div>
              <p className="text-xl font-bold text-white">{formatCurrency(data?.summary?.total_deposits || 0)}</p>
            </div>
            
            <div className="glass-card p-4">
              <div className="flex items-center gap-2 text-amber-400 mb-2">
                <ArrowUpRight className="w-4 h-4" />
                <span className="text-xs">Levantamentos</span>
              </div>
              <p className="text-xl font-bold text-white">{formatCurrency(data?.summary?.total_withdrawals || 0)}</p>
            </div>
            
            <div className="glass-card p-4">
              <div className="flex items-center gap-2 text-blue-400 mb-2">
                <Wallet className="w-4 h-4" />
                <span className="text-xs">Pagamentos</span>
              </div>
              <p className="text-xl font-bold text-white">{formatCurrency(data?.summary?.total_utility_payments || 0)}</p>
            </div>
            
            <div className="glass-card p-4">
              <div className="flex items-center gap-2 text-purple-400 mb-2">
                <RefreshCw className="w-4 h-4" />
                <span className="text-xs">Reinvestimentos</span>
              </div>
              <p className="text-xl font-bold text-white">{formatCurrency(data?.summary?.total_reinvestments || 0)}</p>
            </div>
          </div>

          {/* Filter */}
          <div className="glass-card p-4 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-400">Filtrar por tipo</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant={filterType === 'all' ? 'default' : 'outline'}
                onClick={() => setFilterType('all')}
                className={filterType === 'all' ? 'bg-blue-500' : 'bg-white/5 border-white/10'}
              >
                Todos ({data?.summary?.transaction_count || 0})
              </Button>
              {Object.entries(TYPE_CONFIG).map(([type, config]) => {
                const count = data?.transactions?.filter(t => t.type === type).length || 0;
                if (count === 0) return null;
                return (
                  <Button
                    key={type}
                    size="sm"
                    variant={filterType === type ? 'default' : 'outline'}
                    onClick={() => setFilterType(type)}
                    className={filterType === type ? config.bg.replace('/10', '') : 'bg-white/5 border-white/10'}
                  >
                    {config.label} ({count})
                  </Button>
                );
              })}
            </div>
          </div>

          {/* Transactions Table */}
          <div className="glass-card overflow-hidden">
            <div className="p-4 border-b border-white/10">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-400" />
                <h2 className="text-lg font-semibold text-white">Movimentos</h2>
                <span className="text-sm text-slate-400">({pagination.total_count || filteredTransactions.length})</span>
              </div>
            </div>

            {filteredTransactions.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-white/5 text-left">
                        <th className="p-4 text-xs text-slate-400 font-medium">Data</th>
                        <th className="p-4 text-xs text-slate-400 font-medium">Cliente</th>
                        <th className="p-4 text-xs text-slate-400 font-medium">Tipo</th>
                        <th className="p-4 text-xs text-slate-400 font-medium text-right">Valor</th>
                        <th className="p-4 text-xs text-slate-400 font-medium">Descrição</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {filteredTransactions.map((transaction) => {
                        const config = TYPE_CONFIG[transaction.type] || TYPE_CONFIG.adjustment;
                        const IconComponent = config.icon;
                        return (
                          <tr key={transaction.id} className="hover:bg-white/5 transition-colors">
                            <td className="p-4 text-sm text-slate-300">
                              {formatDate(transaction.created_at)}
                            </td>
                            <td className="p-4">
                              <div>
                                <p className="text-sm text-white">{transaction.user_name}</p>
                                <p className="text-xs text-slate-500">{transaction.user_email}</p>
                              </div>
                            </td>
                            <td className="p-4">
                              <div className={`inline-flex items-center gap-2 px-2 py-1 rounded-lg ${config.bg}`}>
                                <IconComponent className={`w-3 h-3 ${config.color}`} />
                                <span className={`text-xs ${config.color}`}>{config.label}</span>
                              </div>
                            </td>
                            <td className={`p-4 text-sm font-semibold text-right ${
                              ['withdrawal', 'debit'].includes(transaction.type) ? 'text-red-400' : 'text-emerald-400'
                            }`}>
                              {['withdrawal', 'debit'].includes(transaction.type) ? '-' : '+'}
                              {formatCurrency(transaction.amount)}
                            </td>
                            <td className="p-4 text-sm text-slate-400 max-w-xs truncate">
                              {transaction.description}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination.total_pages > 1 && (
                  <div className="p-4 border-t border-white/10">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-slate-400">
                        Página {pagination.page} de {pagination.total_pages} ({pagination.total_count} movimentos)
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPage(1)}
                          disabled={!pagination.has_prev}
                          className="text-slate-400 hover:text-white disabled:opacity-30"
                          data-testid="pagination-first"
                        >
                          <ChevronsLeft className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPage(page - 1)}
                          disabled={!pagination.has_prev}
                          className="text-slate-400 hover:text-white disabled:opacity-30"
                          data-testid="pagination-prev"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <span className="text-sm text-white px-3">{pagination.page}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPage(page + 1)}
                          disabled={!pagination.has_next}
                          className="text-slate-400 hover:text-white disabled:opacity-30"
                          data-testid="pagination-next"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPage(pagination.total_pages)}
                          disabled={!pagination.has_next}
                          className="text-slate-400 hover:text-white disabled:opacity-30"
                          data-testid="pagination-last"
                        >
                          <ChevronsRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="p-12 text-center">
                <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">Sem movimentos neste período</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
